import { Download } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  DialogContent,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { GridCellParams, GridColDef } from "@mui/x-data-grid";
import {} from "common";
import { ReceivingStatus } from "components/atoms";
import { renderProgress } from "components/atoms/PercentProgressCell";
import {
  AppTable,
  DeleteConfirmDialog,
  ImportDocumentDialog,
  ImportErrorDialog,
} from "components/molecules";
import { DownloadHelper, RoleHelper, TableHelper } from "helpers";
import { usePaginationRequest } from "hooks";
import { isArray, isEmpty, isUndefined, toNumber } from "lodash";
import { IDownloadExcelRequest, IPaginationRequest } from "model";
import { IPaginateApiResponse } from "model/Common/IPaginateApiResponse";
import { IReceiveDocument } from "model/IReceive/IReceive";
import moment from "moment";
import { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { DownloadActions, ReceiveActions } from "redux/actions";
import { useAppSelector } from "redux/configurations/reduxHooks";
import ReceiveApiService from "services/ReceiveApiService";
import { CURRENT_PAGE_SIZE, EXPORT_FILE_NAME, FIRST_PAGE } from "utils/Const";
import i18n, { I18nTranslator, translations, _t } from "utils/i18n";
import DeleteIcon from "@mui/icons-material/Delete";
import { theme } from "themes";

enum EImportType {
  IMPORT_NEW,
  UPDATE,
}

const TransactionOutputReport = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [searchText, setSearchText] = useState("");
  const [file, setFile] = useState<FileList | null>(null);
  const [receiveItem, setReceiveItem] = useState<IReceiveDocument | undefined>(
    undefined
  );
  const [importType, setImportType] = useState<EImportType>(
    EImportType.IMPORT_NEW
  );
  const [importFailedDialogVisible, setImportFailedDialogVisible] =
    useState<boolean>(false);
  const isImportNewDocs = importType === EImportType.IMPORT_NEW;
  const [open, setOpen] = useState<boolean>(false);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);

  const data = useAppSelector(
    (state) => state.ReceiveReducer.receive
  ) as IPaginateApiResponse<IReceiveDocument[]>;
  const action = useAppSelector((state) => state.ReceiveReducer.action);
  const errors = useAppSelector((state) => state.ErrorReducer.errors);
  const currentWarehouse = useAppSelector(
    (state) => state.IdentityReducer.currentWarehouse
  );
  const [downloading, setDownloading] = useState(false);
  const [receiveTemplateDownloading, setReceiveTemplateDownloading] =
    useState(false);

  const [request, setRequest] = useState<IPaginationRequest>({
    pageNumber: FIRST_PAGE,
    pageSize: CURRENT_PAGE_SIZE,
    searchText: searchText,
  });
  const handleSelectFile = (files: FileList | null) => {
    if (!isEmpty(files)) {
      setImportType(importType);
      setFile(files);
      setOpen(true);
    }
  };

  const handleCancel = () => {
    setOpen(false);
    setFile(null);
    setReceiveItem(undefined);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
  };

  const onPressDelete = (item: any) => {
    setReceiveItem(item);
    setDeleteOpen(true);
  };

  const handleDeleteReceiveDocument = () => {
    _deleteReceiveDocument(receiveItem?.id!);
  };

  const handleConfirmImport = () => {
    _importReceiveDocument(receiveItem?.id);
  };

  const _deleteReceiveDocument = (id: string) => {
    dispatch(
      ReceiveActions.deleteReceiveDocument.request({
        id: id,
      })
    );
  };
  const _importReceiveDocument = (id?: string) => {
    dispatch(
      ReceiveActions.importReceiveDocument.request({
        file: file?.item(0) as File,
        stockReceivingHeaderId: isEmpty(id) ? "" : id,
      })
    );
  };

  const importDocsSuccess = action.includes(
    ReceiveActions.importReceiveDocument.successName
  );
  const importDocsFailed = action.includes(
    ReceiveActions.importReceiveDocument.failedName
  );
  const deleteDocsSuccess = action.includes(
    ReceiveActions.deleteReceiveDocument.successName
  );
  const deleteDocsFailed = action.includes(
    ReceiveActions.deleteReceiveDocument.successName
  );

  const deleteDocsSuccessOrFailed = deleteDocsSuccess || deleteDocsFailed;

  const handleCellClick = (event: GridCellParams<any, any, any>) => {
    if (event.colDef.field !== "action") {
      history.push(
        `${location.pathname}/${event.row.id}&${event.row.stockReceivingCode}`
      );
    }
  };

  useEffect(() => {
    // _getAllReceiveDocument();
  }, []);

  const { loading, result } = usePaginationRequest<
    IPaginationRequest,
    IReceiveDocument[]
  >(
    request,
    ReceiveApiService.getAllReceives,
    [],
    {
      refresh: refresh,
    },
    () => {
      setRefresh(false);
    }
  );

  const handleChangePage = (pageNumber: number) => {
    setRequest({
      ...request,
      pageNumber,
    });
  };

  const handleSearch = (searchText: string) => {
    setRequest({
      ...request,
      searchText,
    });
  };
  const _handlePageSizeChange = (pageSize: number) => {
    setRequest({ ...request, pageSize: pageSize });
  };

  const _downloadStockReceiving = () => {
    const params: IDownloadExcelRequest = {
      ...request,
      pageSize: (result as IPaginateApiResponse<IReceiveDocument[]>)
        .totalRecords,
      pageNumber: FIRST_PAGE,
      fileName: EXPORT_FILE_NAME.receive,
    };
    dispatch(DownloadActions.downloadReceiveDocument.request(params));
  };
  const _downloadReceiveForm = () => {
    const params: IDownloadExcelRequest = {
      fileName: EXPORT_FILE_NAME.receiveForm,
    };
    dispatch(DownloadActions.downloadReceiveForm.request(params));
  };

  useEffect(() => {
    if (deleteDocsSuccessOrFailed) {
      setRefresh(true);
      setDeleteOpen(false);
    }
  }, [deleteDocsSuccessOrFailed, searchText]);

  useEffect(() => {
    if (importDocsSuccess) {
      setRefresh(true);
      handleCancel();
    }
    if (importDocsFailed) {
      if (isArray(errors) || !isUndefined(errors)) {
        setOpen(false);
        // setImportFailedDialogVisible(true);
      }
    }
  }, [action]);

  const COLUMNS: GridColDef[] = [
    TableHelper.renderSTT(),
    TableHelper.createCell({
      field: "action1",
      size: 100,
      headerName: I18nTranslator(translations.report.action),
      renderCell: ({ row }) => (
        <div style={{ width: "100%", textAlign: "center" }}>
          <IconButton
            sx={{
              display: !(RoleHelper.isSuperAdmin() || RoleHelper.isSupervisor())
                ? "none"
                : "inline",
            }}
            disabled={!(RoleHelper.isSuperAdmin() || RoleHelper.isSupervisor())}
            onClick={() => onPressDelete(row)}
          >
            <DeleteIcon
              sx={{ color: theme.colors.error.light }}
              fontSize="small"
            />
          </IconButton>
        </div>
      ),
      sortable: false,
      disableColumnMenu: true,
    }),
    TableHelper.createCell({
      field: "stockReceivingCode",
      headerName: I18nTranslator(translations.receive.receiveCode),
      size: "small",
      align: "left",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "createdDateTime",
      headerName: I18nTranslator(translations.receive.receiveDate),
      size: "small",
      align: "left",
      sortable: true,
      renderCell: ({ value }) => (
        <Typography>{moment(value).format("DD/MM/YYYY")}</Typography>
      ),
    }),

    TableHelper.createCell({
      field: "companyName",
      headerName: I18nTranslator(translations.receive.vendor),
      size: "full",
      align: "left",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "importStatus",
      headerName: I18nTranslator(translations.receive.status),
      size: "small",
      align: "left",
      renderCell: ReceivingStatus,
    }),
    TableHelper.createCell({
      field: "quantityPlanning",
      headerName: I18nTranslator(translations.receive.planningQuantity),
      size: "small",
      align: "right",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "quantityReceived",
      headerName: I18nTranslator(translations.receive.receivedQuantity),
      size: "small",
      align: "right",
      sortable: true,
    }),
    TableHelper.createCell({
      field: "palletPlanning",
      headerName: I18nTranslator(translations.receive.planPallet),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "palletReceived",
      headerName: I18nTranslator(translations.receive.actualPallet),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "palletSetLocation",
      headerName: I18nTranslator(translations.receive.putOnPallet),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "verifyProgress",
      headerName: I18nTranslator(translations.receive.receivingProgress),
      size: "small",
      align: "left",
      sortable: true,
      renderCell: ({ row }) =>
        renderProgress(
          toNumber(
            ((row.quantityReceived / row.quantityPlanning) * 100).toFixed(0) ||
              0
          )
        ),
    }),

    TableHelper.createCell({
      field: "userName",
      headerName: I18nTranslator(translations.receive.createBy),
      size: "small",
      align: "left",
    }),
  ];

  return (
    <Fragment>
      <AppTable<IReceiveDocument>
        title={i18n.t(_t(translations.receive.receiveDocument))}
        loading={loading}
        data={result}
        columns={COLUMNS}
        actionColumnProps={{
          hideEdit: true,
        }}
        onFileSelected={handleSelectFile}
        onCellClick={handleCellClick}
        onSearch={handleSearch}
        searchPlaceholder={
          "Tìm kiếm: Mã phiếu, mã lô, mã sản phẩm, tên sản phẩm"
        }
        onChangePage={handleChangePage}
        hideAddNew
        onRowPressDelete={onPressDelete}
        onRowPressImport={(row) => {
          setImportType(EImportType.UPDATE);
          setReceiveItem(row);
        }}
        onPageSizeChange={_handlePageSizeChange}
        onDownloadClick={_downloadStockReceiving}
        isDownloading={downloading}
        headerButtonsRight={
          <Stack direction={"row"} spacing={2} sx={{ marginLeft: "10px" }}>
            <LoadingButton
              loading={receiveTemplateDownloading}
              startIcon={<Download />}
              onClick={_downloadReceiveForm}
              variant="contained"
            >
              {i18n.t(_t(translations.button.exportTemplate))}
            </LoadingButton>
          </Stack>
        }
      />
      <ImportDocumentDialog
        title={I18nTranslator(
          isImportNewDocs
            ? translations.receive.importDocs
            : translations.receive.updateDocs
        )}
        open={open}
        handleConfirm={handleConfirmImport}
        handleClose={handleCancel}
        loading={action.includes(
          ReceiveActions.importReceiveDocument.requestName
        )}
        fileName={!isEmpty(file) && file ? file[0].name : ""}
        isImportNewDocs={isImportNewDocs}
        updateItemName={receiveItem?.stockReceivingCode || ""}
      />

      <ImportErrorDialog
        title={I18nTranslator(translations.receive.importError)}
        open={importFailedDialogVisible}
        handleReImport={() => setImportFailedDialogVisible(false)}
      >
        <DialogContent sx={{ maxHeight: "50vh" }}>
          {!isUndefined(errors) &&
            isArray(errors) &&
            (errors as string[]).map((error) => {
              return <Typography>{error}</Typography>;
            })}
        </DialogContent>
      </ImportErrorDialog>
      <DeleteConfirmDialog
        open={deleteOpen}
        value={receiveItem?.stockReceivingCode}
        handleClose={handleDeleteClose}
        handleDelete={handleDeleteReceiveDocument}
        isDeleting={action.includes(
          ReceiveActions.deleteReceiveDocument.requestName
        )}
      />
    </Fragment>
  );
};

export default TransactionOutputReport;
